import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import { IMPACT_CASES, IMPACT_ORDER } from "../data/impactCases";
import StatCountUp from "../components/StatCountUp";

const reveal = { initial: { opacity: 0, y: 40 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" }, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } };

const GLANCE = [
  { n: "01", big: "$7.56M", label: "ESTIMATED MEMBERSHIP VALUE",
    body: "27,000 members acquired through digital advertising at a $280 membership fee.",
    tag: "Scouts Canada · Ditch the Screens", slug: "ditch-the-screens" },
  { n: "02", big: "$700K", label: "ANNUAL FUNDRAISING REVENUE",
    body: "Scout Popcorn grew from approximately $200K to $700K in annual revenue. 250% revenue growth.",
    tag: "Scouts Canada · Scout Popcorn", slug: "scout-popcorn" },
  { n: "03", big: "2,000+", label: "VOLUNTEER LEADS",
    body: "Generated through national volunteer recruitment.",
    tag: "Scouts Canada", slug: "scouts-volunteer-recruitment" },
  { n: "04", big: "CHIPMUNKS", label: "NEW EARLY YEARS PROGRAM",
    body: "Developed from naming and positioning through internal testing and pilot development.",
    tag: "Scouts Canada", slug: "chipmunks" },
  { n: "05", big: "$961 USD", label: "RAISED",
    body: "Generated through a one-week Giving Tuesday campaign using $100 CAD in paid media.",
    tag: "Raise Chess Academy", slug: "raise-chess-giving-tuesday" },
];

const CAPS = [
  { n: "01", t: "Strategy", items: ["Campaign Strategy", "Audience Research", "Brand Positioning", "Program Development"] },
  { n: "02", t: "Story", items: ["Creative Direction", "Campaign Concepts", "Film", "Photography", "Copywriting"] },
  { n: "03", t: "Growth", items: ["Membership Acquisition", "Fundraising", "Volunteer Recruitment", "Lead Generation", "Paid Media"] },
  { n: "04", t: "Journey", items: ["Landing Pages", "Email + CRM", "Lifecycle Marketing", "Lead Nurture", "Conversion", "Onboarding"] },
];

export default function Impact() {
  const featured = ["ditch-the-screens", "scout-popcorn"].map((s) => IMPACT_CASES[s]);
  const supporting = ["scouts-volunteer-recruitment", "chipmunks", "raise-chess-giving-tuesday"].map((s) => IMPACT_CASES[s]);

  return (
    <main data-testid="impact-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] w-full overflow-hidden noise flex items-center bg-black">
        <div className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/8613089/pexels-photo-8613089.jpeg?w=2400&q=85')" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/70 to-black" />
        <div className="relative z-10 max-w-[1600px] mx-auto w-full px-6 md:px-12 lg:px-16 pt-40 pb-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <motion.div {...reveal} className="md:col-span-8">
            <p className="overline mb-8">Good Bandit / Impact</p>
            <h1 className="font-display text-6xl md:text-8xl lg:text-9xl text-white leading-[0.92]">
              Stories that
              <br />move people
              <br /><span className="italic text-[var(--gb-gold)]">to act.</span>
            </h1>
          </motion.div>
          <motion.div {...reveal} className="md:col-span-4 text-white/65 text-sm leading-relaxed space-y-5">
            <p className="text-white/85">Strategy, creative and growth for organizations with something worth building.</p>
            <p>Good Bandit helps nonprofits, community organizations and purpose-led brands grow membership, recruit volunteers, raise funds, launch new programs and build stronger communities.</p>
            <a href="#glance" className="gb-link inline-flex items-center gap-2 mt-4 text-[var(--gb-gold)] text-xs uppercase tracking-[0.25em]">
              Explore the work <ArrowDown size={12} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* GLANCE */}
      <section id="glance" className="border-t border-white/5 bg-black scroll-mt-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <motion.p {...reveal} className="overline mb-3">Section 02</motion.p>
          <motion.h2 {...reveal} className="font-display text-4xl md:text-6xl text-white leading-[0.95] mb-20">
            Impact <span className="italic text-[var(--gb-gold)]">at a glance.</span>
          </motion.h2>

          <div className="divide-y divide-white/10">
            {GLANCE.map((g) => (
              <motion.div {...reveal} key={g.n} className="grid grid-cols-1 md:grid-cols-12 gap-6 py-12 md:py-16" data-testid={`glance-${g.n}`}>
                <div className="md:col-span-1 kbd text-white/40">{g.n}</div>
                <div className="md:col-span-5">
                  <p className="font-display text-6xl md:text-8xl text-white leading-[0.9]">
                    <StatCountUp value={g.big} />
                  </p>
                  <p className="kbd text-[var(--gb-gold)] mt-4">{g.label}</p>
                </div>
                <div className="md:col-span-5 md:col-start-8 text-white/65 text-sm md:text-base leading-relaxed">
                  <p>{g.body}</p>
                  <Link to={`/impact/${g.slug}`} className="gb-link inline-flex items-center gap-2 mt-6 text-white/50 text-xs uppercase tracking-[0.25em] hover:text-[var(--gb-gold)]">
                    {g.tag} <ArrowRight size={12} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SELECTED */}
      <section className="border-t border-white/5 bg-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <motion.p {...reveal} className="overline mb-3">Section 03</motion.p>
          <motion.h2 {...reveal} className="font-display text-4xl md:text-6xl text-white leading-[0.95] mb-16">
            Selected <span className="italic text-[var(--gb-gold)]">Impact.</span>
          </motion.h2>

          {featured.map((c, i) => (
            <motion.div {...reveal} key={c.slug} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center mb-24 ${i%2 ? "md:[&>*:first-child]:order-2" : ""}`}>
              <Link to={`/impact/${c.slug}`} className="md:col-span-7 group relative aspect-[4/3] overflow-hidden bg-black block" data-testid={`featured-${c.slug}`}>
                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-[1500ms] group-hover:scale-[1.03]" style={{ backgroundImage: `url(${c.cover})` }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <p className="absolute top-6 left-6 kbd text-[var(--gb-gold)]">FEATURED 0{i+1}</p>
              </Link>
              <div className="md:col-span-5">
                <p className="kbd text-white/50 mb-4">{c.client}</p>
                <h3 className="font-display text-5xl md:text-6xl text-white leading-[0.95]">
                  {c.heroTitle.map((l, k) => <span key={k} className={k === c.heroTitle.length - 1 ? "italic text-[var(--gb-gold)] block" : "block"}>{l}</span>)}
                </h3>
                <div className="mt-8 space-y-2 text-white/80">
                  {c.heroStats?.slice(0, 2).map((s, k) => (
                    <p key={k} className="text-lg md:text-xl">
                      <span className="text-[var(--gb-gold)]"><StatCountUp value={s.big} /></span>
                      <span className="kbd text-white/50 ml-3">{s.label}</span>
                    </p>
                  ))}
                </div>
                <p className="kbd text-white/40 mt-6">{c.category}</p>
                <p className="text-xs text-white/50 mt-3 tracking-[0.2em]">{c.capabilities.slice(0, 6).map(x => x.toUpperCase()).join(" · ")}</p>
                <Link to={`/impact/${c.slug}`} className="gb-btn-outline inline-flex mt-8 text-[10px]">
                  View case study <ArrowRight size={12} />
                </Link>
              </div>
            </motion.div>
          ))}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-8 border-t border-white/10 pt-16">
            {supporting.map((c, i) => (
              <motion.div {...reveal} key={c.slug}>
                <Link to={`/impact/${c.slug}`} className="group block" data-testid={`support-${c.slug}`}>
                  <div className="relative aspect-[4/5] overflow-hidden bg-black">
                    <div className="absolute inset-0 bg-cover bg-center opacity-60 group-hover:opacity-75 group-hover:scale-[1.02] transition-all duration-[1200ms]" style={{ backgroundImage: `url(${c.cover})` }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                    <p className="absolute top-5 left-5 kbd text-[var(--gb-gold)]">PROJECT 0{i+3}</p>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <p className="kbd text-white/60 mb-2">{c.client}</p>
                      <h4 className="font-display text-3xl md:text-4xl text-white leading-[1] group-hover:text-[var(--gb-gold)] transition-colors duration-500">
                        {c.heroTitle.map((l, k) => <span key={k} className="block">{l}</span>)}
                      </h4>
                      <p className="kbd text-white/40 mt-4">{c.category}</p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="border-t border-white/5 bg-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <motion.p {...reveal} className="overline mb-3">Section 04</motion.p>
          <motion.h2 {...reveal} className="font-display text-4xl md:text-6xl text-white leading-[0.95] mb-20">
            From the idea
            <br /><span className="italic text-[var(--gb-gold)]">to the action.</span>
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-6">
            {CAPS.map((c) => (
              <motion.div {...reveal} key={c.n} className="md:border-r md:border-white/10 md:pr-6 last:border-r-0">
                <p className="kbd text-white/40">{c.n}</p>
                <h3 className="font-display text-3xl text-white mt-3 mb-5">{c.t}</h3>
                <ul className="space-y-2 text-white/65 text-sm">
                  {c.items.map((i) => <li key={i}>{i}</li>)}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NOCTURNE CROSS-LINK */}
      <section className="border-t border-white/5 bg-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <motion.div {...reveal} className="md:col-span-7">
            <p className="overline mb-4">Also from the studio</p>
            <h3 className="font-display text-3xl md:text-5xl text-white leading-[1]">
              Some stories build <span className="italic text-white/60">desire.</span>
              <br />Others build <span className="italic text-[var(--gb-gold)]">movements.</span>
            </h3>
          </motion.div>
          <motion.div {...reveal} className="md:col-span-4 md:col-start-9">
            <p className="text-white/60 text-sm leading-relaxed">Nocturne is Good Bandit's editorial expression — cinematic automotive storytelling in film, photography and publishing.</p>
            <Link to="/nocturne" className="gb-btn-outline mt-6 inline-flex text-[10px]">Enter Nocturne <ArrowRight size={12} /></Link>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-white/5 bg-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-32 md:py-40 text-center">
          <motion.h2 {...reveal} className="font-display text-6xl md:text-8xl text-white leading-[0.95]">
            Build something
            <br /><span className="italic text-[var(--gb-gold)]">that matters.</span>
          </motion.h2>
          <motion.p {...reveal} className="text-white/60 text-sm md:text-base leading-relaxed max-w-2xl mx-auto mt-10">
            Whether the goal is growing membership, rebuilding a fundraiser, finding the next generation of volunteers or launching something entirely new — we build the strategy, story and journey that move people toward action.
          </motion.p>
          <Link to="/contact" className="gb-btn-primary inline-flex mt-12" data-testid="impact-final-cta">
            Start a project <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
